import { useState } from 'react';
import { IAudioCreation } from '../../@types';
import { useDeleteCreatedAudio, useEditCreatedAudio } from '../audios';
import { useSnackbar } from 'notistack';
import { useDialog } from '../useDialog';
import { useRouter } from 'next/router';

interface Props {
  refetchData: () => void;
}

export const useAudioCreationManager = ({ refetchData }: Props) => {
  const { push } = useRouter();
  const [selectedAudio, setSelectedAudio] = useState<IAudioCreation | null>(null);

  const {
    isDialogOpen: isDeleteDialogOpen,
    openDialog: openDeleteDialog,
    closeDialog: closeDeleteDialog,
  } = useDialog();
  const {
    isDialogOpen: isRenameDialogOpen,
    openDialog: openRenameDialog,
    closeDialog: closeRenameDialog,
  } = useDialog();

  const { enqueueSnackbar } = useSnackbar();
  const { mutate: mutateDeleteAudio, isLoading: isDeleteLoading } = useDeleteCreatedAudio();
  const { mutate: mutateEditName, isLoading: isEditNameLoading } = useEditCreatedAudio();

  const handleOpenRenameDialog = (audio: IAudioCreation) => {
    setSelectedAudio(audio);
    openRenameDialog();
  };

  const handleDownload = (audio: IAudioCreation) => {
    const link = document.createElement('a');
    link.href = audio.processedURL;
    link.download = `${audio.name}.mp3`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleOpenDeleteDialog = (audio: IAudioCreation) => {
    setSelectedAudio(audio);
    openDeleteDialog();
  };

  const handleViewAll = () => {
    push('/text-to-audio');
  };

  const handleRename = (name: string) => {
    mutateEditName(
      { id: selectedAudio?.id, name },
      {
        onSuccess: () => {
          enqueueSnackbar('Audio data updated successfully!', { variant: 'success' });
          setSelectedAudio(null);
          closeRenameDialog();
          refetchData();
        },
        onError: (error: any) => {
          enqueueSnackbar(error?.message || 'Failed to update audio data. Please try again.', {
            variant: 'error',
          });
          closeRenameDialog();
        },
      }
    );
  };

  const handleConfirmDelete = () => {
    if (selectedAudio) {
      mutateDeleteAudio(
        { id: selectedAudio?.id },
        {
          onSuccess: () => {
            enqueueSnackbar('Audio deleted successfully!', { variant: 'success' });
            setSelectedAudio(null);
            closeDeleteDialog();
            refetchData();
          },
          onError: (error: any) => {
            enqueueSnackbar(error?.message || 'Failed to delete audio. Please try again.', {
              variant: 'error',
            });
            closeDeleteDialog();
          },
        }
      );
    } else {
      enqueueSnackbar('No audio selected for deletion.', { variant: 'warning' });
      closeDeleteDialog();
    }
  };

  const handlePreview = (data: IAudioCreation) => {
    push(`/text-to-audio/${data.id}`);
  };

  return {
    selectedAudio,
    isDeleteDialogOpen,
    isRenameDialogOpen,
    closeRenameDialog,
    handleOpenRenameDialog,
    handleDownload,
    handleOpenDeleteDialog,
    handleConfirmDelete,
    handleRename,
    closeDeleteDialog,
    isDeleteLoading,
    isEditNameLoading,
    handleViewAll,
    handlePreview,
  };
};
